import { cloneElement, createContext, ReactElement, useMemo } from 'react';
import {
  Divider,
  useTheme,
  makeStyles,
  useMediaQuery
} from '@material-ui/core';
import {
  CapaPurchaseOrderStatus,
  CapaQuotationRfqPurchaseOrder,
  FetchPurchaseOrderPaymentData,
  ShippingDataModel
} from '@generated/graphql';
import {
  CustomizedTypography,
  ConditionalRender
} from '@components/Customized';
import { colorSet } from '@components/Provider';
import { ProgressInfoQA } from './ProgressInfoQA';
import { ProgressInfoHeader } from './ProgressInfoHeader';
import Paid from './ProgressInfoBottomItem/Paid';
import Shipping from './ProgressInfoBottomItem/Shipping';
import ProcessPayment from './ProgressInfoBottomItem/ProcessPayment';
import RefundConfirmed from './ProgressInfoBottomItem/RefundConfirmed';
import RequestedWithReason from './ProgressInfoBottomItem/RequestedWithReason';
import PurchaseConfirmed from './ProgressInfoBottomItem/PurchaseConfirmed';

interface ProgressInfoCardProps {
  purchaseOrder?: CapaQuotationRfqPurchaseOrder;
  processStatus?: CapaPurchaseOrderStatus;
  payment: FetchPurchaseOrderPaymentData;
  shipping?: ShippingDataModel[];
}

const ProgressInfoCard = ({
  purchaseOrder,
  payment,
  processStatus,
  shipping
}: ProgressInfoCardProps) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { progressInfoConatiner, progressInfoDivider } = useSytles();
  const cancelReason = purchaseOrder?.paymentCancelData?.reason ?? undefined;

  const detailRender = useMemo(() => {
    switch (processStatus) {
      case CapaPurchaseOrderStatus.ProcessPayment:
        return <ProcessPayment />;

      case CapaPurchaseOrderStatus.Paid:
        return <Paid />;

      case CapaPurchaseOrderStatus.RefundRequested:
      case CapaPurchaseOrderStatus.CancelRequested:
      case CapaPurchaseOrderStatus.RejectOrderByPartner:
        return <RequestedWithReason cancelReason={cancelReason} />;

      case CapaPurchaseOrderStatus.RefundConfirmed:
      case CapaPurchaseOrderStatus.CancelConfirmed:
      case CapaPurchaseOrderStatus.CancelledByAdmin:
        return <RefundConfirmed />;

      case CapaPurchaseOrderStatus.Processing:
        return <ProgressInfoQA />;

      case CapaPurchaseOrderStatus.Shipping:
        return <Shipping shipping={shipping} />;

      case CapaPurchaseOrderStatus.PurchaseConfirmed:
        return <PurchaseConfirmed purchaseOrder={purchaseOrder} />;

      default:
        return null;
    }
  }, [processStatus, cancelReason, shipping, purchaseOrder]);

  if (!processStatus || processStatus === CapaPurchaseOrderStatus.Created)
    return null;

  return (
    <ProgressStateContext.Provider value={processStatus}>
      <div className={progressInfoConatiner}>
        <ProgressInfoHeader
          processStatus={processStatus}
          isExtended={shipping?.[0]?.isExtended}
        />

        <ConditionalRender
          visible={processStatus === CapaPurchaseOrderStatus.ProcessPayment}
        >
          <CustomizedTypography variant="body2" color="textSecondary">
            {Intl.DateTimeFormat('kr', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }).format(payment?.virtualAccount?.dueDate)}
            까지 입금
          </CustomizedTypography>
        </ConditionalRender>

        <ConditionalRender visible={detailRender !== null}>
          <Divider className={progressInfoDivider} />

          {detailRender &&
            cloneElement(detailRender as ReactElement, {
              payment,
              matches,
              purchaseOrderStatus: processStatus,
              purchaseOrder
            })}
        </ConditionalRender>
      </div>
    </ProgressStateContext.Provider>
  );
};

export default ProgressInfoCard;

const useSytles = makeStyles((theme) => ({
  progressInfoConatiner: {
    display: 'flex',
    flexDirection: 'column',
    width: 'calc(100% - 48px)',
    borderRadius: 12,
    padding: 24,
    gap: 16,
    border: `1px solid ${colorSet.gray300}`,
    boxShadow: `0px 8px 36px 4px rgba(0, 0, 0, 0.04)`,
    [theme.breakpoints.down('md')]: {
      width: 'calc(100% - 32px)'
    }
  },
  progressInfoDivider: {
    height: 1,
    width: '100%',
    backgroundColor: colorSet.gray300
  }
}));

export const ProgressStateContext = createContext<CapaPurchaseOrderStatus | null>(
  null
);
